import axios from 'axios';

export interface FilterOption {
  value: string;
  label: string;
}

interface OptionsBatchResponse {
  data: { results: Record<string, FilterOption[]> };
}

/**
 * Coalesces every filter dropdown's options request fired in the same tick into
 * one `GET /reporting-2.0/options-batch?dimensions=…`. The filter bar mounts six
 * dropdowns at once; this turns six requests into one (mirrors the report
 * batcher). Each caller still resolves with just its own dimension's options.
 */
interface Pending {
  dimension: string;
  resolve: (options: FilterOption[]) => void;
  reject: (error: unknown) => void;
}

let queue: Pending[] = [];
let scheduled = false;

/**
 * Dropdown options are Snowflake-backed. Warm, these return in ~2s (measured on
 * the advertiser list, ~900 distinct names) — but a suspended warehouse has to
 * resume first, which can take the best part of a minute. 60s covers that cold
 * start with headroom; the previous 150s meant a genuinely hung request left the
 * filter bar spinning for two and a half minutes before giving up
 * (review, flank#3817).
 */
const OPTIONS_TIMEOUT_MS = 60_000;

const flush = async (): Promise<void> => {
  const batch = queue;
  queue = [];
  scheduled = false;
  if (batch.length === 0) return;

  const dimensions = [...new Set(batch.map(p => p.dimension))];
  try {
    const response = await axios.get<OptionsBatchResponse>('/reporting-2.0/options-batch', {
      params: { dimensions: dimensions.join(',') },
      timeout: OPTIONS_TIMEOUT_MS,
    });
    const results = response.data?.data?.results ?? {};
    for (const pending of batch) pending.resolve(results[pending.dimension] ?? []);
  } catch (error) {
    for (const pending of batch) pending.reject(error);
  }
};

export const enqueueOptions = (dimension: string): Promise<FilterOption[]> =>
  new Promise<FilterOption[]>((resolve, reject) => {
    queue.push({ dimension, resolve, reject });
    if (!scheduled) {
      scheduled = true;
      setTimeout(flush, 0);
    }
  });

interface OptionsResponse {
  data: { options: FilterOption[] };
}

/**
 * What a scoped dropdown's options are narrowed by. The API narrows downward
 * only; `clients` (#4737) and `ribeyeCampaignIds` (#4614) are read for their
 * own dimensions alone. Tenancy is always applied server-side on top.
 */
export interface OptionsScope {
  /** Advertiser names — the advertiser dropdown's values (Campaign / Line Item). */
  advertisers?: string[];
  /** Whitelabel ids (Client, #4454). */
  whitelabels?: string[];
  /** Client ids — narrows Advertiser (#4737) and the ribeye dimensions (#4614). */
  clients?: string[];
  /** Ribeye campaign ids — narrows `ribeye_line_item` (#4614). */
  ribeyeCampaignIds?: string[];
}

const SCOPE_PARAM: Record<keyof OptionsScope, string> = {
  advertisers: 'advertisers',
  whitelabels: 'whitelabels',
  clients: 'clients',
  ribeyeCampaignIds: 'ribeye_campaign_ids',
};

/**
 * Query params for a scope. Every value is JSON-encoded — advertiser names can
 * contain commas — and empty fields are left out, since `[]` and absent both
 * mean "no narrowing".
 */
export const scopeParams = (scope: OptionsScope): Record<string, string> => {
  const params: Record<string, string> = {};
  for (const [key, param] of Object.entries(SCOPE_PARAM) as Array<[keyof OptionsScope, string]>) {
    const values = scope[key];
    if (values?.length) params[param] = JSON.stringify(values);
  }
  return params;
};

/**
 * Fetch options for a single dimension narrowed by `scope` — used for Campaign /
 * Line Item (advertiser + cascade scope), Client (whitelabel scope, #4454), and
 * Advertiser (client scope, #4737) so their dropdowns only list entities tied
 * to the chosen parents. Not batched (the scope changes with the selection,
 * unlike the mount-once dropdowns).
 */
export const fetchScopedOptions = async (
  dimension: string,
  scope: OptionsScope,
  search?: string,
): Promise<FilterOption[]> => {
  const response = await axios.get<OptionsResponse>('/reporting-2.0/options', {
    params: { dimension, ...scopeParams(scope), ...(search ? { search } : {}) },
    timeout: OPTIONS_TIMEOUT_MS,
  });
  return response.data?.data?.options ?? [];
};

/**
 * Typeahead search for one dimension. The option list is capped server-side, so
 * filtering the already-fetched page client-side can't reach anything past the
 * cap — Line Item was cut off inside "A". This asks the server to do the match,
 * still narrowed by the dropdown's scope.
 */
export const searchOptions = (dimension: string, search: string, scope: OptionsScope = {}): Promise<FilterOption[]> =>
  fetchScopedOptions(dimension, scope, search);
